import React, { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Icons, SVG } from '../../../components/atoms/SVG';
import { Tooltip, TooltipPlacement } from '../../../components/atoms/Tooltip';
import ResultListSettings from '../../../components/molecules/ResultListSettings/ResultListSettings';
import { SearchFormResults } from './SearchFormResults';
import Btn, { BtnModifier } from '../../../components/molecules/Btn';
import { Badge, BadgeModifier } from '../../../_lib/components/Badge';
import { createExtender } from '../../../lib/bemUtils/bem';
import { murmurhash } from '../../../lib/js/hash';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { SectionTypes } from '../../../types/api/Sections';
import { SearchResult } from '../Search.types';
import { isNotNullOrUndefined } from '../../../lib/typeGuards';
import { CollapsibleWysiwygView } from '../../../_lib/components/Wysiwyg';
import { FetchType } from '../../../types/api/FetchType';
import { FACETS } from '../../../components/molecules/ResultListSettings/ResultListSettings.enums';

type SearchForm = {
  searchResult: SearchResult;
  type: 'content' | 'user';
  showSwitchButtons?: boolean;
  wantedFacets: FACETS[];
};

export function SearchForm({ searchResult, type, showSwitchButtons, wantedFacets }: SearchForm) {
  const { t } = useSonTranslation('SearchForm');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [focus, setFocus] = useState<boolean>(false);
  const [hasInputValue, setHasInputValue] = useState<boolean>(searchResult.term !== '' || false);
  const formRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resetButtonRef = useRef(null);

  const checkInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isNotNullOrUndefined(event.target.value) && event.target.value !== '') {
      setHasInputValue(true);
    } else {
      setHasInputValue(false);
    }
  };
  const fqPathId = searchResult.sectionFilter?.sectionParams?.fq_pathId;

  const performSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    searchParams.delete('page'); // new search means resetting the pagination
    if (inputRef.current && inputRef.current.value) {
      searchParams.set('q', inputRef.current?.value);
    }
    // forcing refetch, so a search with the same term can be sent out multiple times
    navigate(`?${searchParams.toString()}`, {
      state: {
        refetch: 'force',
        omitSection: [SectionTypes.personal, SectionTypes.navigation, SectionTypes.breadcrumb, SectionTypes.footer],
      },
    });
  };

  const onClickReset = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    if (inputRef.current) {
      inputRef.current.focus();
      // need to empty the value in the field
      inputRef.current.value = '';
      setHasInputValue(false);
    }
  };

  const getSwitchingButtons = () => {
    if (searchResult.searchLinks) {
      return (
        <div className="search-form__switching-buttons">
          <h3 className="invisible">{t('search.moreResults')}</h3>
          {Object.entries(searchResult.searchLinks).map(([key, link]) => {
            const id = murmurhash(link);
            const badgeTitle =
              link.count > 1 || link.count === 0
                ? t('search.results_plural')
                : t('search.results', { count: link.count });
            return (
              <div key={key} className="search-form__switching-button">
                <Btn
                  href={key === type ? undefined : link.url}
                  modifier={key === type ? BtnModifier.ACTIVE : undefined}
                  disabledLink={key === type}
                  ariaDescribedBy={id}
                >
                  <span className="search-form__switching-button-content">
                    <span className="search-form__switching-button-content-text">
                      {t(`search.type.${key as 'content' | 'user'}`)}
                    </span>
                    <span className="search-form__switching-button-badge" id={id} aria-hidden="true">
                      <Badge
                        modifier={
                          key === type ? [BadgeModifier.CONTENT, BadgeModifier.CONTENT_ACTIVE] : BadgeModifier.CONTENT
                        }
                        title={`${link.count} ${badgeTitle}`}
                      >
                        <span className="search-form__switching-button-badge-count">{link.count}</span>
                        <span className="invisible">{badgeTitle}</span>
                      </Badge>
                    </span>
                  </span>
                </Btn>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };

  const renderResetButton = () => {
    const extender = createExtender('search-form__reset-wrapper');
    const modifier = [];

    if (focus && hasInputValue) {
      modifier.push('search-form__reset-wrapper--visible');
    } else {
      modifier.push('search-form__reset-wrapper--invisible');
    }
    return (
      <Tooltip attachTo={resetButtonRef} placement={TooltipPlacement.BOTTOM}>
        <span className={extender(modifier)}>
          <button
            type="reset"
            ref={resetButtonRef}
            className="search-form__reset"
            title={t('search.deletesearch')}
            tabIndex={focus && hasInputValue ? 0 : -1}
            // Events
            onClick={e => onClickReset(e)}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
          >
            <span className="invisible">{t('search.deletesearch')}</span>
            <SVG svgSpriteId={Icons.DECLINE_CROSS} inverted svgClass="search-form__svg-reset" />
          </button>
        </span>
      </Tooltip>
    );
  };

  return (
    <div className="search-form">
      <form
        // needs to be reactivated if action url should point to another endpoint
        action={searchResult.searchForm.url}
        onSubmit={event => performSearch(event)}
        role="search"
        className="search-form__form"
        ref={formRef}
        method="GET"
      >
        {fqPathId && <input type="hidden" name="fq_pathId" value={fqPathId} />}
        <div className="search-form__padded-container">
          <p className="search-form__form-container">
            <span className="search-form__input-container">
              <input
                ref={inputRef}
                type="search"
                className="search-form__input"
                id={`${type}-search`}
                name="q"
                placeholder={t('search.title')}
                required
                minLength={2}
                title={t('search.title')}
                aria-label={t('search.label')}
                maxLength={searchResult.maxLength}
                autoComplete="off"
                spellCheck="false"
                aria-describedby="search-results-text"
                defaultValue={searchResult.term || ''}
                onChange={event => checkInputValue(event)}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
              />
              {renderResetButton()}
            </span>
            <button type="submit" className="search-form__button">
              <span className="search-form__text-container">{t('search.search')}</span>
              <span className="search-form__icon-container">
                <SVG svgSpriteId={Icons.SEARCH_MAGNIFIER} svgClass="search-form__icon-search" />
              </span>
            </button>
          </p>
          {searchResult.term && <SearchFormResults searchResult={searchResult} />}
          {searchResult.hasUnsupportedSonSearchFeatures && (
            <p className="search-form__warning">{t('search.unsupportedSonSearchFeatures')}</p>
          )}
        </div>
        {showSwitchButtons && getSwitchingButtons()}
        {searchResult?.richtext && (
          <div className="search-form__help-text">
            <CollapsibleWysiwygView
              fetchType={FetchType.instant}
              href={searchResult.searchForm.url}
              embedded={searchResult?.richtext}
            />
          </div>
        )}
        <ResultListSettings
          searchStrictness={searchResult.searchStrictness}
          facets={searchResult.facets}
          sort={searchResult.sort}
          filter={searchResult.filter}
          resetAllFiltersUrl={searchResult.resetAllFiltersUrl}
          wantedFacets={wantedFacets}
        />
      </form>
    </div>
  );
}
